import { useState, useEffect } from "react";
import Sidebar from "../components/layout/Sidebar";
import Header from "../components/layout/Header";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function AdminStaffAssessment() {
  const [staff, setStaff] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      const res = await api.get('/admin/staff-assessment');
      setStaff(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStaffClick = (id: string) => {
    navigate(`/admin/staff-assessment/${id}`);
  };

  return (
    <div className="min-h-screen bg-surface text-on-surface">
      <Header />
      <Sidebar />
      <main className="ml-0 md:ml-20 pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
          {/* Header */}
          <div className="flex flex-col space-y-2">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Operational Auditing</span>
            </div>
            <h2 className="text-5xl font-black text-on-surface tracking-tighter leading-tight">
              Staff <span className="text-outline-variant/40">Assessment.</span>
            </h2>
            <p className="text-slate-500 font-medium">Evaluate execution efficiency and response metrics across the staff hierarchy.</p>
          </div>

          {/* Staff List Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {loading ? (
              [1, 2, 3].map(i => (
                <div key={i} className="h-64 bg-surface-container-low rounded-[2.5rem] animate-pulse" />
              ))
            ) : staff.length > 0 ? (
              staff.map((s) => (
                <div 
                  key={s.id} 
                  onClick={() => handleStaffClick(s.id)}
                  className="bg-surface-container-low p-8 rounded-[2.5rem] border border-outline-variant/10 shadow-sm hover:shadow-2xl hover:scale-[1.02] transition-all cursor-pointer group relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:scale-110 transition-transform">
                    <span className="material-symbols-outlined text-7xl text-primary fill-1">assessment</span>
                  </div>

                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary font-black text-xl">
                      {s?.name?.[0] || "S"}
                    </div>
                    <div>
                      <h4 className="text-lg font-black text-on-surface tracking-tight">{s.name}</h4>
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{s.role.replace("_", " ")}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="space-y-1">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Tasks Resolved</p>
                      <p className="text-2xl font-black text-on-surface">{s.completedCount}</p>
                    </div>
                    <div className="space-y-1 text-right">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Health Score</p>
                      <p className="text-2xl font-black text-emerald-500">98%</p>
                    </div>
                  </div>

                  <div className="space-y-4 pt-6 border-t border-outline-variant/10">
                    <div className="flex justify-between items-center text-[10px] font-bold text-slate-500">
                      <span>Avg. Response Time</span>
                      <span className="text-on-surface font-black">{s.avgTimeToAttend}m</span>
                    </div>
                    <div className="flex justify-between items-center text-[10px] font-bold text-slate-500">
                      <span>Avg. Resolution Time</span>
                      <span className="text-on-surface font-black">{s.avgTimeToComplete}m</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full py-20 text-center space-y-4 opacity-30">
                 <span className="material-symbols-outlined text-6xl">person_search</span>
                 <p className="text-xl font-black tracking-tight">No tactical personnel found for assessment.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
